import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  daysBetween,
  emptyCorrectiveAction,
  normalizePlanAction,
  userMetaLabel,
  validateCorrectiveAction,
} from "./correctiveActionUtils";

const buildDraft = (action, ordre) => ({
  ...emptyCorrectiveAction(ordre),
  ...(action || {}),
  responsable_ids: Array.isArray(action?.responsable_ids)
    ? [...action.responsable_ids]
    : action?.responsable_id
      ? [action.responsable_id]
      : [],
  responsables: Array.isArray(action?.responsables)
    ? [...action.responsables]
    : [],
});

const CorrectiveActionDialog = ({
  action,
  nextOrder,
  onClose,
  onSave,
  open,
  saving,
  users,
  usersError,
  usersLoading,
}) => {
  const [draft, setDraft] = useState(() => buildDraft(action, nextOrder));
  const [errors, setErrors] = useState({});
  const planActionRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDraft(buildDraft(action, nextOrder));
    setErrors({});
  }, [action, nextOrder, open]);

  const selectedResponsables = useMemo(() => {
    const savedResponsables = Array.isArray(draft.responsables)
      ? draft.responsables
      : [];
    return (draft.responsable_ids || []).map((responsableId) => {
      const activeUser = users.find(
        (user) => String(user.id) === String(responsableId)
      );
      if (activeUser) return activeUser;
      const saved = savedResponsables.find(
        (responsable) =>
          String(responsable.responsable_id) === String(responsableId)
      );
      return {
        id: responsableId,
        label: saved?.responsable_name || `Utilisateur #${responsableId}`,
      };
    });
  }, [draft.responsable_ids, draft.responsables, users]);

  const delay = daysBetween(draft.date_debut, draft.date_fin);

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const restorePlanCursor = (cursor) => {
    window.requestAnimationFrame(() => {
      const input = planActionRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(cursor, cursor);
    });
  };

  const handlePlanChange = (event) => {
    const normalized = normalizePlanAction(
      event.target.value,
      event.target.selectionStart
    );
    updateDraft("plan_action", normalized.value);
    restorePlanCursor(normalized.cursor);
  };

  const handlePlanFocus = () => {
    if (String(draft.plan_action || "").length) return;
    updateDraft("plan_action", "1. ");
    restorePlanCursor(3);
  };

  const handleSubmit = async () => {
    const nextErrors = validateCorrectiveAction(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    await onSave(draft);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={saving ? undefined : onClose}
      open={open}
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "92vh" } }}
    >
      <DialogTitle sx={{ color: "#173d61", fontWeight: 800 }}>
        {action?.plan_action_id
          ? "Modifier l’action corrective"
          : "Ajouter une action corrective"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              disabled={saving}
              error={Boolean(errors.action_corrective)}
              fullWidth
              helperText={errors.action_corrective}
              label="Action corrective"
              minRows={4}
              multiline
              onChange={(event) =>
                updateDraft("action_corrective", event.target.value)
              }
              value={draft.action_corrective}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled={saving}
              error={Boolean(errors.plan_action)}
              fullWidth
              helperText={errors.plan_action || "Chaque ligne est numérotée automatiquement."}
              inputRef={planActionRef}
              label="Plan d’action"
              minRows={4}
              multiline
              onBlur={() => {
                const normalized = normalizePlanAction(
                  draft.plan_action,
                  String(draft.plan_action || "").length
                );
                if (normalized.value !== draft.plan_action) {
                  updateDraft("plan_action", normalized.value);
                }
              }}
              onChange={handlePlanChange}
              onFocus={handlePlanFocus}
              value={draft.plan_action}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled={saving}
              error={Boolean(errors.indicateur_efficacite)}
              fullWidth
              helperText={errors.indicateur_efficacite}
              label="Indicateur d’efficacité"
              minRows={3}
              multiline
              onChange={(event) =>
                updateDraft("indicateur_efficacite", event.target.value)
              }
              value={draft.indicateur_efficacite}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              disableCloseOnSelect
              disabled={saving || usersLoading || Boolean(usersError)}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                String(option.id) === String(value.id)
              }
              limitTags={3}
              loading={usersLoading}
              loadingText="Chargement des utilisateurs…"
              multiple
              noOptionsText="Aucun utilisateur actif"
              onChange={(_, options) => {
                updateDraft(
                  "responsable_ids",
                  options.map((option) => option.id)
                );
                setDraft((current) => ({
                  ...current,
                  responsables: options.map((option) => ({
                    responsable_id: option.id,
                    responsable_name: option.label,
                  })),
                }));
              }}
              options={users}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(errors.responsable_ids || usersError)}
                  helperText={errors.responsable_ids || usersError}
                  label="Responsables"
                  placeholder="Choisir un ou plusieurs responsables"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontSize={13} fontWeight={700}>
                      {option.label}
                    </Typography>
                    {userMetaLabel(option) && (
                      <Typography color="text.secondary" fontSize={11}>
                        {userMetaLabel(option)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              value={selectedResponsables}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              disabled={saving}
              error={Boolean(errors.avancement)}
              fullWidth
              helperText={errors.avancement}
              inputProps={{ max: 100, min: 0 }}
              label="Avancement (%)"
              onChange={(event) => updateDraft("avancement", event.target.value)}
              type="number"
              value={draft.avancement}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              InputLabelProps={{ shrink: true }}
              disabled={saving}
              error={Boolean(errors.date_debut)}
              fullWidth
              helperText={errors.date_debut}
              inputProps={{ max: "9999-12-31" }}
              label="Date de début"
              onChange={(event) => updateDraft("date_debut", event.target.value)}
              type="date"
              value={draft.date_debut}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              InputLabelProps={{ shrink: true }}
              disabled={saving}
              error={Boolean(errors.date_fin)}
              fullWidth
              helperText={errors.date_fin}
              inputProps={{ max: "9999-12-31" }}
              label="Date de fin"
              onChange={(event) => updateDraft("date_fin", event.target.value)}
              type="date"
              value={draft.date_fin}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              inputProps={{ readOnly: true }}
              label="Délai"
              value={delay !== "" && delay >= 0 ? `${delay} jour(s)` : "—"}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={saving} onClick={onClose}>
          Annuler
        </Button>
        <Button
          disabled={saving || usersLoading || Boolean(usersError)}
          onClick={handleSubmit}
          startIcon={
            saving ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          variant="contained"
        >
          Enregistrer l’action
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CorrectiveActionDialog.propTypes = {
  action: PropTypes.object,
  nextOrder: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  usersError: PropTypes.string,
  usersLoading: PropTypes.bool.isRequired,
};

CorrectiveActionDialog.defaultProps = {
  action: null,
  usersError: null,
};

export default CorrectiveActionDialog;
